import { useEffect, useState } from 'react';
import { Form, Input, Button, message, Typography, Card, Avatar, Steps, Select } from 'antd';
import Heading from '../../common/Heading';
import coreAPIService from '../../apiServices/coreAPIService';
import { CTW_API_ENDPOINT } from '../../utils';
import { useBreadcrumbs } from '../../context/BreadcrumbContext';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '../../utils/errorHandler';
import { SaveOutlined, PlusOutlined, ShopOutlined, UserOutlined, MailOutlined } from '@ant-design/icons';
import { useAuthOrg } from '../../context/AuthOrgContext';
import { uploadMedia } from '../../utils/campaignUtils';
import { Upload } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { hasRolePermission } from '../../utils/roles';

const { Title, Text, Paragraph } = Typography;

interface Brand {
  brandDbId: number;
  brandId: number;
  brandName: string;
  website: string;
  logoUrl: string;
  orgId: string;
  orgName: string;
  isVerified: boolean;
  createdAt: string;
  about?: string;
}

interface CreateBrandRequest {
  orgId: string;
  business_entity_id: number;
  brand_name: string;
  website: string;
  audience_location: string[];
  languages: string[];
  social_media_handles: {
    facebook: string;
    instagram: string;
    twitter: string;
    whatsapp: string;
  };
  about: string;
  online_store: string;
  mau: number;
  site_category: string[];
  other_category: string;
  brandLogo: string;
}

interface CreateBrandResponse {
  meta: {
    status: boolean;
    message: string;
    code: number;
    newToken: string | null;
  };
  data?: {
    brand_id: number;
  };
}

export default function VerismartSetup() {
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [brandCreated, setBrandCreated] = useState(false); // For self-served flow
  const navigate = useNavigate();
  const [brandForm] = Form.useForm();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [uploading, setUploading] = useState(false);

  // Multi-step onboarding state (for self-served flow)
  const [currentStep, setCurrentStep] = useState(0);
  const [signupForm] = Form.useForm();
  const [companyForm] = Form.useForm();
  const [apiKey, setApiKey] = useState('');
  const [brandId, setBrandId] = useState<number | null>(null);
  const [stepOneValues, setStepOneValues] = useState<{ signup: any; brand: any } | null>(null);

  // Get org details from context
  const { currentOrg, user, userRole, availableOrgs } = useAuthOrg();
  const isSelfServed = user?.userMode === 'self-served';
  const isManagedAdmin = user?.userMode === 'managed' && hasRolePermission(userRole, 'accessSetup');

  // Dummy brands data (for managed flow)
  const [brands, setBrands] = useState<Brand[]>([
    {
      brandDbId: 5,
      brandId: 722,
      brandName: "New brand",
      website: "",
      logoUrl: "https://antigravity.google/assets/image/antigravity-logo.png",
      orgId: "c5be48b9-dc3d-11ee-8854-0281fa2d601d",
      orgName: "Times mobile",
      isVerified: false,
      createdAt: "2025-12-30T11:07:12"
    }
  ]);

  useEffect(() => {
    setBreadcrumbs([
      {
        title: <div className="text-lg cursor-pointer" onClick={() => navigate(`/audience`)}>KYB</div>,
      },
    ]);

    // Check brand status for self-served flow
    if (isSelfServed) {
      checkBrandStatus();
    }

    // Fetch all brands for managed admin users
    if (isManagedAdmin || isSelfServed) {
      fetchAllBrands();
    }
  }, [setBreadcrumbs, navigate, isSelfServed, isManagedAdmin]);

  const fetchAllBrands = async () => {
    try {
      setLoading(true);
      const res = await coreAPIService.get<{ meta: { status: boolean, code: number }, data: any[] }>(
        CTW_API_ENDPOINT,
        "v2/verismart/get-all-brands"
      );
      if (res.meta.status && res.data) {
        setBrands(res.data);
      } else if (res.meta.code === 200 && res.data) {
        setBrands(res.data);
      }
    } catch (error) {
      console.error('Error fetching all brands:', error);
      message.error('Failed to fetch brands');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrgs = availableOrgs;

  const checkBrandStatus = async () => {
    try {
      setLoading(true);
      const res = await coreAPIService.get<{ meta: { status: boolean, code: number } }>(
        CTW_API_ENDPOINT,
        "v2/verismart/is-brand-available"
      );
      if (res.meta.status) {
        setBrandCreated(true);
      }
    } catch (error) {
      console.error('Error checking brand status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle company profile form submission - Self-served flow
  const handleCompanySubmit = async () => {
    try {
      await signupForm.validateFields();
      await brandForm.validateFields();
      await companyForm.validateFields();
      setLoading(true);

      const signupValues = stepOneValues?.signup || signupForm.getFieldsValue();
      const brandValues = stepOneValues?.brand || brandForm.getFieldsValue();
      const companyValues = companyForm.getFieldsValue();


      const payload = {
        name: signupValues.display_name,
        email: signupValues.email,
        brand_name: brandValues.brand_name,
        website: brandValues.website || '',
        brand_logo: brandValues.brandLogo || '',
        languages: brandValues.languages || ['en'],
        social_media_handles: brandValues.social_media_handles || {
          facebook: '',
          instagram: '',
          twitter: '',
          whatsapp: ''
        },
        about: brandValues.about || '',
        site_category: brandValues.site_category || [],
        company_name: companyValues.company_name,
        gst_no: companyValues.gst_no || '',
        contact_no: companyValues.contact_no || '',
        company_address: companyValues.company_address || '',
        pan_no: companyValues.pan_no || '',
        director_name: companyValues.director_name || '',
        nature_of_business: companyValues.nature_of_business || ''
      };

      try {
        const response = await coreAPIService.post<{ meta: { status: boolean; code: number; message: string } }>(
          CTW_API_ENDPOINT,
          'v2/verismart/kyb',
          payload,
        );

        if (response.meta.code === 200) {
          message.success('Onboarding completed successfully!');
          setBrandCreated(true);
        } else {
          message.error(response.meta.message || 'Failed to complete onboarding');
        }
      } catch (apiError: any) {
        console.error('API Error:', apiError);
        message.error(`API Error: ${apiError.response?.data?.message || apiError.message || 'Unknown error'}`);
      }
    } catch (error) {
      message.error('Please fill all required fields');
    } finally {
      setLoading(false);
    }
  };

  const handleBrandSubmit = async () => {
    try {
      await brandForm.validateFields();
      setLoading(true);

      const values = brandForm.getFieldsValue();

      // Construct payload with only required fields
      const brandPayload: CreateBrandRequest = {
        orgId: values.orgId || currentOrg?.orgId || '',
        business_entity_id: -1,
        brand_name: values.brand_name,
        website: '',
        audience_location: ['IN'],
        brandLogo: values.brandLogo || '',
        languages: [],
        social_media_handles: {
          facebook: '',
          instagram: '',
          twitter: '',
          whatsapp: ''
        },
        about: values.about || "",
        online_store: '',
        mau: 0,
        site_category: [],
        other_category: ''
      };

      const response = await coreAPIService.post<CreateBrandResponse>(
        CTW_API_ENDPOINT,
        'v2/verismart/create-brand',
        brandPayload
      );

      if (response.meta.code === 200) {
        message.success('Brand created successfully!');

        if (isSelfServed) {
          // Self-served flow: mark brand as created
          setBrandCreated(true);
        } else {
          // Managed flow: add to brand list and close form
          setShowCreateForm(false);
          brandForm.resetFields();
          const newBrand: Brand = {
            brandDbId: response.data?.brand_id || 0,
            brandId: response.data?.brand_id || 0,
            brandName: values.brand_name,
            logoUrl: values.brandLogo,
            website: '',
            orgId: values.orgId || currentOrg?.orgId || '',
            orgName: currentOrg?.orgName || '',
            isVerified: false,
            createdAt: new Date().toISOString(),
            about: values.about
          };
          setBrands([...brands, newBrand]);
        }
      } else {
        message.error(response.meta.message || 'Failed to create brand');
      }

    } catch (error: any) {
      if (error?.errorFields) {
        message.error('Please fill all required fields');
      } else {
        handleApiError(error, 'create-brand', 'Failed to create brand');
      }
    } finally {
      setLoading(false);
    }
  };

  // Self-Served Flow Render - Complete Multi-Step Onboarding
  const renderSelfServedFlow = () => (
    <section className="res-px py-4 md:py-8">
      <div className="bg-white shadow-md rounded-lg p-4 md:p-8">
        <div className="mb-6 md:mb-10 text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Brand Onboarding
          </h1>
          {!brandCreated && <p className="text-sm md:text-base text-gray-500">
            Complete the following steps to set up your brand and company profile
          </p>}
        </div>

        {!brandCreated && <div className="mb-8 md:mb-12 overflow-x-auto pb-4">
          <div className="min-w-[600px] px-4">
            <Steps
              current={currentStep}
              items={[
                {
                  title: 'Account & Brand Details',
                  description: 'Basic information',
                },
                {
                  title: 'Company Profile',
                  description: 'Business details',
                },
              ]}
              className="brand-setup-steps"
            />
          </div>
        </div>}

        {brandCreated ? (
          brands.map((brand, index) => (
            <Card
              key={`${brand.brandId || brand.brandDbId}-${index}`}
              hoverable
              className="rounded-lg shadow-sm hover:shadow-md transition-all h-full"
            >
              <div className="flex flex-col items-center text-center h-full">
                <Avatar
                  size={64}
                  src={brand.logoUrl}
                  icon={<ShopOutlined />}
                  className="mb-4 flex-shrink-0"
                />
                <Title level={5} className="mb-1 text-sm md:text-base truncate w-full">{brand.brandName}</Title>
                <Text type="secondary" className="text-[10px] md:text-xs mb-3">ID: {brand.brandId || brand.brandDbId}</Text>
                <Paragraph
                  ellipsis={{ rows: 3 }}
                  className="text-xs md:text-sm text-gray-600 mb-0 flex-1"
                >
                  {brand.about || `Brand ID: ${brand.brandId || brand.brandDbId}`}
                </Paragraph>
              </div>
            </Card>
          ))
        ) : (
          <div className="bg-white p-4 md:p-8 rounded-xl border border-gray-100">
            {currentStep === 0 ? (
              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                  <Card title="Brand Information" className="shadow-sm" size="small">
                    <Form
                      form={brandForm}
                      layout="vertical"
                      initialValues={{
                        brand_name: '',
                        orgId: currentOrg?.orgId || '',
                        website: '',
                        audience_location: [],
                        brandLogo: '',
                        languages: [],
                        social_media_handles: { facebook: '', instagram: '', twitter: '', whatsapp: '' },
                        about: '',
                        online_store: '',
                        mau: null,
                        site_category: [],
                      }}
                    >
                      <Form.Item label="Brand Logo" name="brandLogo" rules={[{ required: true, message: 'Please upload a brand logo' }]}>
                        <div className="flex items-center gap-4">
                          <Form.Item name="brandLogo">
                            <Input hidden />
                          </Form.Item>
                          <Upload
                            name="logo"
                            listType="picture-card"
                            className="avatar-uploader w-full h-10"
                            showUploadList={false}
                            beforeUpload={async (file) => {
                              const isImage = file.type.startsWith('image/');
                              if (!isImage) {
                                message.error('You can only upload image files!');
                                return Upload.LIST_IGNORE;
                              }
                              const isLt2M = file.size / 1024 / 1024 < 2;
                              if (!isLt2M) {
                                message.error('Image must be smaller than 2MB!');
                                return Upload.LIST_IGNORE;
                              }

                              try {
                                setUploading(true);
                                const orgId = brandForm.getFieldValue('orgId') || currentOrg?.orgId;
                                if (!orgId) {
                                  message.error('Account ID is missing');
                                  setUploading(false);
                                  return Upload.LIST_IGNORE;
                                }

                                const url = await uploadMedia(file, orgId, '');
                                if (url) {
                                  brandForm.setFieldsValue({ brandLogo: url });
                                  message.success('Logo uploaded successfully');
                                }
                              } catch (error) {
                                message.error('Failed to upload logo');
                              } finally {
                                setUploading(false);
                              }
                              return false;
                            }}
                          >
                            {brandForm.getFieldValue('brandLogo') ? (
                              <img src={brandForm.getFieldValue('brandLogo')} alt="logo" style={{ width: '100%' }} />
                            ) : (
                              <div>
                                {uploading ? <LoadingOutlined /> : <PlusOutlined />}
                                <div style={{ marginTop: 8 }}>Upload</div>
                              </div>
                            )}
                          </Upload>
                        </div>
                      </Form.Item>
                      <Form.Item className="mt-20" label="Account" name="orgId" rules={[{ required: true, message: 'Please select an account' }]}>
                        <Select placeholder="Select Account" className="h-10">
                          {filteredOrgs.map(org => (
                            <Select.Option key={org.orgId} value={org.orgId}>{org.orgName}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item label="Brand Name" name="brand_name" rules={[{ required: true }]}>
                        <Input className="h-10" />
                      </Form.Item>
                      <Form.Item label="About" name="about" rules={[{ required: true }]}>
                        <Input.TextArea rows={3} />
                      </Form.Item>
                    </Form>
                  </Card>

                  <div className="flex flex-col gap-6">
                    <Card title="Account Information" className="shadow-sm" size="small">
                      <Form
                        form={signupForm}
                        layout="vertical"
                        initialValues={{ is_partner: true, role_name: 'NORMAL_USER' }}
                      >
                        <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
                          <Input prefix={<MailOutlined className="text-gray-400" />} className="h-10" />
                        </Form.Item>
                        <Form.Item label="Display Name" name="display_name" rules={[{ required: true }]}>
                          <Input prefix={<UserOutlined className="text-gray-400" />} className="h-10" />
                        </Form.Item>
                        <Form.Item label="Role Name" name="role_name" hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item label="Is Partner" name="is_partner" hidden>
                          <Input />
                        </Form.Item>
                      </Form>
                    </Card>

                    <div className="mt-auto">
                      <Button
                        type="primary"
                        size="large"
                        onClick={async () => {
                          try {
                            await signupForm.validateFields();
                            await brandForm.validateFields();
                            setStepOneValues({
                              signup: signupForm.getFieldsValue(),
                              brand: brandForm.getFieldsValue(),
                            });
                            setCurrentStep(1);
                          } catch (error) {
                            message.error('Please fill all required fields');
                          }
                        }}
                        loading={loading}
                        className="w-full h-12 text-lg font-medium rounded-lg shadow-md"
                      >
                        Continue to Company Profile
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto">
                <Card title="Company Profile" className="shadow-sm" size="small">
                  <Form
                    form={companyForm}
                    layout="vertical"
                    initialValues={{
                      company_name: '',
                      gst_no: '',
                      contact_no: '',
                      company_address: '',
                      pan_no: '',
                      director_name: '',
                      director_pan: '',
                      director_aadhar: '',
                      nature_of_business: '',
                      gst_certificate: '',
                      company_pan_certificate: '',
                      director_pan_certificate: '',
                      director_aadhar_certificate: '',
                    }}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <Form.Item label="Company Name" name="company_name" rules={[{ required: true }]}>
                        <Input className="h-10" />
                      </Form.Item>
                      <Form.Item label="GST No" name="gst_no">
                        <Input className="h-10" />
                      </Form.Item>
                      <Form.Item label="Contact No" name="contact_no">
                        <Input className="h-10" />
                      </Form.Item>
                      <Form.Item label="PAN No" name="pan_no">
                        <Input className="h-10" />
                      </Form.Item>
                      <Form.Item label="Director Name" name="director_name">
                        <Input className="h-10" />
                      </Form.Item>
                      <Form.Item label="Director PAN" name="director_pan">
                        <Input className="h-10" />
                      </Form.Item>
                      <Form.Item label="Director Aadhar" name="director_aadhar">
                        <Input className="h-10" />
                      </Form.Item>
                      <Form.Item label="Nature of Business" name="nature_of_business">
                        <Input className="h-10" />
                      </Form.Item>
                      <Form.Item label="Company Address" name="company_address" className="md:col-span-2 lg:col-span-3">
                        <Input.TextArea rows={3} />
                      </Form.Item>
                    </div>
                  </Form>
                </Card>

                <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8">
                  <Button
                    size="large"
                    onClick={() => setCurrentStep(0)}
                    className="w-full sm:w-auto px-10 h-12 rounded-lg"
                  >
                    Back
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleCompanySubmit}
                    loading={loading}
                    className="w-full sm:w-auto px-10 h-12 text-lg font-medium rounded-lg shadow-md"
                  >
                    Complete Onboarding
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );

  // Managed Flow Render
  const renderManagedFlow = () => (
    <section className="res-px py-4 md:py-8">
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-4 md:p-8 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Heading title="Brand Setup" className="mb-1" />
            <Text type="secondary" className="text-sm">Manage your brands and create new ones.</Text>
          </div>
          {!showCreateForm && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                if (filteredOrgs.length === 0) {
                  message.info("All available accounts already have a brand associated with them.");
                } else {
                  setShowCreateForm(true);
                }
              }}
              className="w-full sm:w-auto bg-[#1F344C] text-base font-medium rounded-lg shadow-sm hover:shadow-md transition-all"
            >
              Add New Brand
            </Button>
          )}
        </div>

        {showCreateForm ? (
          <div className="p-4 md:p-8">
            <div className="mb-6 flex items-center justify-between">
              <Title level={4} className="mb-0 text-lg md:text-xl">Add New Brand</Title>
            </div>

            <Form
              form={brandForm}
              layout="vertical"
              size="large"
              initialValues={{
                business_entity_id: -1,
                orgId: currentOrg?.orgId || '',
                brand_name: '',
                brandLogo: '',
                about: ''
              }}
              onFinish={handleBrandSubmit}
            >
              {/* Hidden Field for Business Entity ID */}
              <Form.Item name="business_entity_id" hidden>
                <Input />
              </Form.Item>

              <div className="w-full">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Left Column: Brand Logo */}
                  <div className="w-full md:w-auto flex-shrink-0">
                    <Form.Item
                      label="Brand Logo"
                      name="brandLogo"
                      rules={[{ required: true, message: 'Please upload a brand logo' }]}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Form.Item name="brandLogo" noStyle>
                          <Input hidden />
                        </Form.Item>
                        <Upload
                          name="logo"
                          listType="picture-card"
                          className="avatar-uploader"
                          showUploadList={false}
                          disabled={!brandForm.getFieldValue('orgId') && !currentOrg?.orgId}
                          beforeUpload={async (file) => {
                            const isImage = file.type.startsWith('image/');
                            if (!isImage) {
                              message.error('You can only upload image files!');
                              return Upload.LIST_IGNORE;
                            }
                            const isLt2M = file.size / 1024 / 1024 < 2;
                            if (!isLt2M) {
                              message.error('Image must be smaller than 2MB!');
                              return Upload.LIST_IGNORE;
                            }
                            try {
                              setUploading(true);
                              const orgId = brandForm.getFieldValue('orgId') || currentOrg?.orgId;
                              if (!orgId) {
                                message.error('Please select an account first');
                                setUploading(false);
                                return Upload.LIST_IGNORE;
                              }

                              const url = await uploadMedia(file, orgId, '');
                              if (url) {
                                brandForm.setFieldsValue({ brandLogo: url });
                                message.success('Logo uploaded successfully');
                              }
                            } catch (error) {
                              message.error('Failed to upload logo');
                            } finally {
                              setUploading(false);
                            }
                            return false;
                          }}
                        >
                          {brandForm.getFieldValue('brandLogo') ? (
                            <img src={brandForm.getFieldValue('brandLogo')} alt="logo" style={{ width: '100%' }} />
                          ) : (
                            <div>
                              {uploading ? <LoadingOutlined /> : <PlusOutlined />}
                              <div style={{ marginTop: 8 }}>Upload</div>
                            </div>
                          )}
                        </Upload>
                        {(!brandForm.getFieldValue('orgId') && !currentOrg?.orgId) && <span className="text-xs text-gray-400 text-center">Select account first</span>}
                      </div>
                    </Form.Item>
                  </div>

                  {/* Right Column: Account and Brand Name */}
                  <div className="flex-1 mt-6">
                    <Form.Item layout='horizontal' label="Account" name="orgId" rules={[{ required: true, message: 'Please select an account' }]}>
                      <Select placeholder="Select Account" className="h-11">
                        {filteredOrgs.map(org => (
                          <Select.Option key={org.orgId} value={org.orgId}>{org.orgName}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label="Brand Name"
                      name="brand_name"
                      layout='horizontal'
                      rules={[{ required: true, message: 'Please enter brand name' }]}
                    >
                      <Input placeholder="e.g. My Brand" className="h-11" />
                    </Form.Item>
                  </div>
                </div>

                <Form.Item
                  label="About Brand"
                  name="about"
                  rules={[{ required: true, message: 'Please enter brand description' }]}
                >
                  <Input.TextArea rows={4} placeholder="Tell us about your brand..." className="rounded-lg" />
                </Form.Item>
              </div>

              <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8 pt-6 border-t border-gray-100">
                <Button type="default" onClick={() => setShowCreateForm(false)} className="h-11 rounded-lg order-2 sm:order-1">
                  Cancel
                </Button>
                <Button
                  // type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  className="h-11 px-8 text-base bg-[#1F344C] text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all order-1 sm:order-2"
                >
                  Save Brand
                </Button>
              </div>
            </Form>
          </div>
        ) : (
          <div className="p-4 md:p-8 bg-gray-50">
            <Title level={5} className="mb-6 text-gray-700 text-base md:text-lg">Onboarded Brands ({brands.length})</Title>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {brands.map((brand, index) => (
                <Card
                  key={`${brand.brandId || brand.brandDbId}-${index}`}
                  hoverable
                  className="rounded-lg shadow-sm hover:shadow-md transition-all h-full"
                >
                  <div className="flex flex-col items-center text-center h-full">
                    <Avatar
                      size={64}
                      src={brand.logoUrl}
                      icon={<ShopOutlined />}
                      className="mb-4 flex-shrink-0"
                    />
                    <Title level={5} className="mb-1 text-sm md:text-base truncate w-full">{brand.brandName}</Title>
                    <Text type="secondary" className="text-xs text-blue-600"><b>Account ID: </b>{brand.orgName}</Text>
                    {/* <Text type="secondary" className="text-[10px] md:text-xs mb-3">ID: {brand.brandId || brand.brandDbId}</Text> */}
                    <Paragraph
                      ellipsis={{ rows: 3 }}
                      className="text-xs md:text-sm text-gray-600 mb-0 flex-1"
                    >
                      {brand.about || `Brand ID: ${brand.brandId || brand.brandDbId}`}
                    </Paragraph>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );

  return isSelfServed ? renderSelfServedFlow() : renderManagedFlow();
}
